import { useContext, useEffect, useState } from "react";

import RoutesContext from "context/routes/routesContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { DEVICE_STATISTICS, ENDPOINTS } from "shared/types/enums";
import { StatisticsData } from "shared/types/interfaces";

import useStatisticsStringParams from "./useStatisticsStringParams";

export const useGetDeviceStatistics = (
    statQueryParam: DEVICE_STATISTICS,
    isFromDashboard?: boolean
) => {
    const { isStatisticsOpen, closeStatistics } = useContext(RoutesContext);

    const { queryData, handleResponse, isCanceled, cancelSource } = useApi();
    const isMounted = useIsMounted();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [statItem, setStatItem] = useState<any>(null);

    const statisticsStringParams = useStatisticsStringParams();

    const setUpStatisticsItems = (data: object) => {
        const total = Object.values(data).reduce(
            (sum, { count }) => sum + count,
            0
        );

        if (total !== 0) {
            const initialArray: [string, StatisticsData][] =
                Object.entries(data);

            const sortedArray = [...initialArray].sort(
                ([, aValue], [, bValue]) => bValue.count - aValue.count
            );

            setStatItem({ stats: sortedArray, total });
            setIsLoading(false);
        } else {
            setStatItem(null);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const handleCatcherror = (error: any) => {
            if (!isCanceled(error)) {
                handleResponse(error);
                setIsLoading(false);
                closeStatistics();
            }
        };

        const handleDataRetrieval = async () => {
            try {
                setIsLoading(true);

                const { data } = await queryData(
                    `${ENDPOINTS.Devices}/stats?field=${statQueryParam}`,
                    statisticsStringParams
                );

                setUpStatisticsItems(data);
            } catch (error) {
                handleCatcherror(error);
            }
        };

        const getStats = () => {
            if (isMounted) {
                handleDataRetrieval();
            }
        };

        if (statQueryParam.length > 0) {
            (isStatisticsOpen || isFromDashboard) && getStats();
        } else {
            setIsLoading(false);
        }

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, [isStatisticsOpen, statisticsStringParams]);

    return { isLoading, statItem };
};
